export type TrackingLabels = Record<string, string[]>;

import type {
  DataContainer,
  Feature,
  FeatureStatus,
} from "@otto-ec/tracking-bct";

export interface CinemaFeature {
  id: string;
  name: "Spx-Cinema";
  position: number;
  status: "loaded" | "clicked";
  labels: TrackingLabels;
}

export interface TileFeature {
  id: string;
  parentId: string;
  name: "Spx-Product";
  position: number;
  status: "loaded" | "clicked";
  variationId: string;
  labels: TrackingLabels;
}

export type TrackingFeature = CinemaFeature | TileFeature;

const filterToSpaFeatureStates = (status: string) => {
  if (status === "loaded" || status === "clicked") {
    return status;
  }
  return undefined;
};

export const castTrackingFeatureToFeature = (
  feature: TrackingFeature,
): Feature => ({
  id: feature.id,
  name: feature.name,
  status: filterToSpaFeatureStates(feature.status),
  labels: feature.labels as DataContainer,
  position: feature.position,
  ...("parentId" in feature && feature.parentId
    ? { parentId: feature.parentId }
    : {}),
  ...("variationId" in feature && feature.variationId
    ? { variationId: feature.variationId }
    : {}),
});

export const castTrackingFeaturesToFeatures = (
  features: TrackingFeature[],
): Feature[] => features.map(castTrackingFeatureToFeature);

export function enrichWithViewSuffix(
  labels: TrackingLabels | undefined,
): TrackingLabels {
  if (!labels) return {};
  return Object.fromEntries(
    Object.entries(labels).map(([key, value]) =>
      key.startsWith("spx_") ? [`${key}View`, value] : [key, value],
    ),
  );
}

export function enrichWithClickSuffix(
  labels: TrackingLabels | undefined,
): TrackingLabels {
  if (!labels) return {};
  return Object.fromEntries(
    Object.entries(labels).map(([key, value]) =>
      key.startsWith("spx_") ? [`${key}Click`, value] : [key, value],
    ),
  );
}

export function extractBaseLabels(features: TrackingFeature[]): TrackingLabels {
  const result: TrackingLabels = {};
  features
    .flatMap((feature) => Object.entries(feature.labels))
    .forEach(([key, values]) => {
      result[key] = result[key] ? [...result[key], ...values] : values;
    });
  return result;
}

export function buildCinemaFeatureForView(
  auctionId: string,
  cinemaTrackingLabels: TrackingLabels,
): CinemaFeature {
  return {
    id: auctionId,
    name: "Spx-Cinema",
    position: 99,
    status: "loaded",
    labels: enrichWithViewSuffix(cinemaTrackingLabels),
  };
}

export function buildTileFeatureForView(
  auctionId: string,
  variationId: string,
  position: number,
  tileTrackingLabels: TrackingLabels,
): TileFeature {
  return {
    id: `${auctionId}_${position}`,
    parentId: auctionId,
    name: "Spx-Product",
    position,
    status: "loaded",
    variationId,
    labels: enrichWithViewSuffix(tileTrackingLabels),
  };
}

export function buildCinemaFeatureForClick(
  auctionId: string,
  cinemaTrackingLabels: TrackingLabels,
): CinemaFeature {
  return {
    id: auctionId,
    name: "Spx-Cinema",
    position: 99,
    status: "clicked",
    labels: enrichWithClickSuffix(cinemaTrackingLabels),
  };
}

export function buildTileFeatureForClick(
  auctionId: string,
  variationId: string,
  position: number,
  tileTrackingLabels: TrackingLabels,
): TileFeature {
  return {
    id: `${auctionId}_${position}`,
    parentId: auctionId,
    name: "Spx-Product",
    position,
    status: "clicked",
    variationId,
    labels: enrichWithClickSuffix(tileTrackingLabels),
  };
}

export function buildCinemaFeatureForScroll(
  auctionId: string,
  cinemaTrackingLabels: TrackingLabels,
): CinemaFeature {
  return {
    id: auctionId,
    name: "Spx-Cinema",
    position: 99,
    status: "loaded",
    labels: enrichWithViewSuffix(cinemaTrackingLabels),
  };
}

export function buildClickBaseLabels(
  features: TrackingFeature[],
): TrackingLabels {
  const baseLabels = extractBaseLabels(features);
  baseLabels["wk.promo_AttributionFeature"] = ["SpaCinema_DetailView"];
  baseLabels["wk.promo_AttributionSource"] = [" "];
  return baseLabels;
}
